/**
 * 可配置的日志工具
 *
 */

/** 日志级别 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** 日志级别优先级 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * 消息展示接口 (适配 antd message API)
 */
export interface MessageApi {
  info: (content: string) => void;
  warning: (content: string) => void;
  error: (content: string) => void;
  success: (content: string) => void;
}

/** Logger 配置 */
export interface LoggerOptions {
  /** 日志前缀 */
  prefix?: string;
  /** 最低日志级别，低于此级别的日志不会输出 */
  level?: LogLevel;
  /**
   * 是否为生产环境（用于决定是否抑制日志）
   *
   * 默认 false。消费方应根据自身构建工具传入：
   * - UmiJS: `process.env.NODE_ENV === 'production'`
   * - Vite: `import.meta.env.PROD`
   */
  isProduction?: boolean;
  /** 生产环境下是否仍输出所有级别日志，默认 false (仅输出 warn/error) */
  enableAllInProduction?: boolean;
  /** 是否显示时间戳，默认 true */
  showTimestamp?: boolean;
  /**
   * 消息展示 API 实例 (可选，适配 antd message 等)
   *
   * 传入后，info/warn/error/success 方法会同时调用 UI 提示。
   *
   * @example
   * ```ts
   * import { message } from 'antd';
   * const logger = createLogger({ prefix: 'App', messageApi: message });
   * logger.error('操作失败'); // 同时输出到 console 和 antd message
   * ```
   */
  messageApi?: MessageApi;
}

/** Logger 实例 */
export interface Logger {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
  /** 成功日志 (info 级别) */
  success(message: string, ...args: unknown[]): void;
}

/**
 * 创建 Logger 实例
 *
 * @param options - Logger 配置
 * @returns Logger 实例
 *
 * @example
 * ```ts
 * // 基础用法 (纯 console)
 * const logger = createLogger({ prefix: 'Auth' });
 * logger.info('User logged in', { userId: 123 });
 *
 * // 集成 antd message
 * import { message } from 'antd';
 * const logger = createLogger({ prefix: 'App', messageApi: message });
 * logger.error('操作失败'); // console.error + antd message.error
 * logger.success('保存成功'); // console.info + antd message.success
 * ```
 */
export function createLogger(options?: LoggerOptions): Logger {
  const prefix = options?.prefix ?? '';
  const minLevel = options?.level ?? 'debug';
  const isProduction = options?.isProduction ?? false;
  const enableAllInProduction = options?.enableAllInProduction ?? false;
  const showTimestamp = options?.showTimestamp ?? true;
  const messageApi = options?.messageApi;

  function shouldLog(level: LogLevel): boolean {
    if (isProduction && !enableAllInProduction) {
      // 生产环境: 仅 warn 和 error 输出 (安全审计)
      if (level !== 'warn' && level !== 'error') return false;
    }
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[minLevel];
  }

  function formatMessage(level: string, message: string): string {
    const parts: string[] = [];
    if (showTimestamp) {
      parts.push(`[${new Date().toLocaleTimeString()}]`);
    }
    if (prefix) {
      parts.push(`[${prefix}]`);
    }
    parts.push(`[${level.toUpperCase()}]`);
    parts.push(message);
    return parts.join(' ');
  }

  return {
    debug(message: string, ...args: unknown[]) {
      if (!shouldLog('debug')) return;
      console.debug(formatMessage('debug', message), ...args);
    },

    info(message: string, ...args: unknown[]) {
      if (!shouldLog('info')) return;
      console.info(formatMessage('info', message), ...args);
      messageApi?.info(message);
    },

    warn(message: string, ...args: unknown[]) {
      if (!shouldLog('warn')) return;
      console.warn(formatMessage('warn', message), ...args);
      messageApi?.warning(message);
    },

    error(message: string, ...args: unknown[]) {
      if (!shouldLog('error')) return;
      console.error(formatMessage('error', message), ...args);
      messageApi?.error(message);
    },

    success(message: string, ...args: unknown[]) {
      if (!shouldLog('info')) return;
      console.info(formatMessage('✓', message), ...args);
      messageApi?.success(message);
    },
  };
}
